"use client";

import { useRef, useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const vintageMeta = [{
  prefix: "00-06 MO",
  fallbackName: "ARCHITECTURAL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/arch_series.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/arch_series.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/arch_series.webp-wvc-sizes"
}, {
  prefix: "12-18 MO",
  fallbackName: "MONOCHROME",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/mono_series.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/mono_series.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/mono_series.png-wvc-sizes"
}, {
  prefix: "SINGLE LOT",
  fallbackName: "PORTRAITS",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/portrait_series.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/portrait_series.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/portrait_series.png-wvc-sizes"
}, {
  prefix: "24-36 MO",
  fallbackName: "LANDSCAPES",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/landscape_series.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/landscape_series.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/landscape_series.png-wvc-sizes"
}, {
  prefix: "ANNUAL",
  fallbackName: "LIMITED EDITION",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/limited_series.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/limited_series.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/limited_series.webp-wvc-sizes"
}];
function VintageTilesConsumer() {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) {
        setScrollProgress(0);
        return;
      }
      const progress = el.scrollLeft / maxScroll * 100;
      setScrollProgress(progress);
    };
    handleScroll();
    el.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [terms]);
  if (loading) {
    return <div className="flex gap-6 overflow-hidden">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-[600px] w-[320px] flex-shrink-0 bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
        Unable to load collections at this time.
      </p>;
  }
  if (terms.length === 0) {
    return <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
        No collections available.
      </p>;
  }
  return <>
      <div ref={scrollRef} className="flex gap-6 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {terms.map((term, i) => {
        const meta = vintageMeta[i % vintageMeta.length];
        const imageUrl = term.image_url || meta.image;
        const displayName = term.name || meta.fallbackName;
        return <Link key={term.id} data-index={i} to={term.link || "#"} className="group relative block flex-shrink-0 w-[75vw] sm:w-[420px] md:w-[340px] lg:w-[calc((100%-6rem)/5)] aspect-[3/5] overflow-hidden snap-start">
              <div className="absolute inset-0 overflow-hidden">
                <img src={imageUrl} data-wvc-srcset={meta.srcset} data-wvc-sizes={meta.sizes} alt={displayName} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>

              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80 transition-all duration-500 group-hover:to-black/90" />

              <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 flex items-end justify-between gap-4">
                <div className="flex flex-col gap-3 min-w-0">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">
                    {meta.prefix}
                  </span>
                  <h3 className="font-default font-bold uppercase text-2xl md:text-[32px] leading-[0.95] tracking-tight text-white break-words">
                    {displayName}
                  </h3>
                </div>

                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 border border-white bg-transparent backdrop-blur-sm transition-all duration-300 group-hover:bg-white group-hover:border-white">
                  <ArrowUpRight className="w-5 h-5 text-white transition-colors duration-300 group-hover:text-black" />
                </div>
              </div>
            </Link>;
      })}
      </div>

      <div className="mt-12 w-full h-px bg-white/20 relative overflow-hidden">
        <div className="absolute left-0 top-0 h-full bg-white transition-[width] duration-150 ease-out" style={{
        width: `${Math.max(scrollProgress, 8)}%`
      }} />
      </div>
    </>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 5,
  orderby: "name",
  order: "asc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function Categories() {
  return <section data-section-id="4283" id="curated-categories" className="bg-black text-white py-32 md:py-44 lg:py-[180px] overflow-hidden">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-6 md:gap-8 mb-16 md:mb-20">
            <span className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-gray-500">
              — 04 / COLLECTIONS
            </span>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <h2 className="font-default font-bold uppercase text-white leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[96px]">
                CURATED /
                <br />
                GALLERIES.
              </h2>
              <p className="font-default text-base md:text-lg text-gray-400 max-w-md leading-relaxed">
                Each series represents a deliberate study in light and form, 
                captured on film with uncompromising precision for the discerning collector.
              </p>
            </div>
          </div>

          <EcommerceTermsProvider wp_term_query={wp_term_query} className="vintages-grid-wrapper">
            <VintageTilesConsumer />
          </EcommerceTermsProvider>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500">
              [ SCROLL HORIZONTALLY → ]
            </span>
            <Button asChild variant="outline" className="border border-white bg-transparent text-white hover:bg-white hover:text-black font-mono uppercase tracking-[0.2em] text-xs h-12 px-8">
              <Link to="/ShopPage">View Full Archive</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}